// Sidebar.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from './SocketContext';
import api from '../services/api';

import {
  FaTachometerAlt, FaList, FaCalendarAlt, FaDollarSign, FaCalculator,
  FaFileAlt, FaTasks, FaChevronDown, FaBookOpen,
  FaShieldAlt, FaHeadset, FaUserShield,
  FaRoute, FaCommentDots, FaIdBadge,
  FaParking, FaCog, FaFileInvoiceDollar, FaBuilding,
  FaChartLine, FaClipboardList, FaCalendarCheck,
  FaFileUpload, FaFileDownload, FaFileSignature, FaReceipt, FaWallet,
  FaBalanceScale, FaLandmark, FaClipboardCheck, FaExchangeAlt, FaCreditCard, FaTruck,
  FaHandHoldingUsd, FaCheckDouble, FaPlug, FaGavel, FaShoppingCart,
  FaUserTie, FaIdCard, FaCar, FaPlusCircle, FaStream, FaColumns,
  FaTicketAlt, FaUsersCog, FaNewspaper, FaTags, FaLayerGroup, FaTerminal,
  FaGlobeAmericas, FaUserCheck, FaKey, FaBullhorn, FaBroadcastTower, FaTools,
  FaDesktop, FaRegListAlt, FaNetworkWired, FaUserCheck as FaUserCheckSolid,
  FaWrench, FaGasPump, FaVial, FaFingerprint, FaCode, FaPaperPlane,
  FaMoneyCheckAlt, FaRobot, FaSatelliteDish,
} from 'react-icons/fa';

/* ============================================================================
 * Helpers
 * ============================================================================ */
// [PORTAL] Tenant fixo do Portal Corporativo (espelha PORTAL_TENANT_ID do back).
const PORTAL_TENANT_ID = 1;

const normalizePermissions = (rawPerms) => {
  if (!rawPerms) return [];
  if (rawPerms instanceof Set) return Array.from(rawPerms);
  if (Array.isArray(rawPerms)) return rawPerms;

  if (typeof rawPerms === 'string') {
    try {
      const parsed = JSON.parse(rawPerms);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const isRouteActive = (pathname, targetPath) => {
  if (!targetPath) return false;
  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
};

const userHasPermission = (user, key) => {
  if (!key) return true;
  const perms = user?.permissoes;
  if (!perms) return false;

  if (perms instanceof Set) return perms.has(key);
  if (Array.isArray(perms)) return perms.includes(key);

  return false;
};

const canAccess = (user, accessKey) => {
  if (user?.perfil === 'admin_geral') return true;
  return userHasPermission(user, accessKey);
};

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');

const formatImageUrl = (url, version) => {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  let normalizedUrl = url;

  if (!url.startsWith('http')) {
    let baseURL = api.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:3001';

    if (baseURL.startsWith('/')) {
      baseURL = window.location.origin.replace('3000', '3001') + baseURL;
    }

    baseURL = baseURL.replace(/\/api\/?$/, '');
    normalizedUrl = `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  }

  if (version) {
    normalizedUrl += `${normalizedUrl.includes('?') ? '&' : '?'}v=${version}`;
  }

  return normalizedUrl;
};

/* ============================================================================
 * LogoHeader
 * ============================================================================ */
function LogoHeader({ logoUrl, nome, isCollapsed }) {
  const [imgError, setImgError] = useState(false);
  const initials = getInitials(nome);

  useEffect(() => {
    setImgError(false);
  }, [logoUrl]);

  const showImage = !!logoUrl && !imgError;

  return (
    <div className="sidebar-header">
      {showImage ? (
        <img
          src={logoUrl}
          alt={nome || 'Logo da empresa'}
          className="sidebar-logo"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="sidebar-logo-placeholder" title={nome} aria-label={nome}>
          {initials || <FaBuilding size={16} />}
        </div>
      )}

      {!isCollapsed && (
        <span className="sidebar-title" title={nome || 'Sistema'}>
          {nome || 'Sistema'}
        </span>
      )}
    </div>
  );
}

/* ============================================================================
 * MenuItem
 * ============================================================================ */
const MenuItem = ({
  item,
  isCollapsed,
  openDropdown,
  toggleDropdown,
  handleLinkClick,
  location,
  supportNotification,
  user,
}) => {
  const temPermissaoPai = canAccess(user, item.accessKey);
  const subItemsPermitidos = item.isDropdown && item.subItems
    ? item.subItems.filter((s) => canAccess(user, s.accessKey))
    : [];

  if (!temPermissaoPai && !subItemsPermitidos.length) return null;

  const isDropdownOpen = openDropdown === item.name;
  const isActive = item.isDropdown
    ? subItemsPermitidos.some((s) => isRouteActive(location.pathname, s.path))
    : isRouteActive(location.pathname, item.path);

  if (item.isDropdown) {
    return (
      <li className={`menu-item dropdown ${isActive ? 'active' : ''}`}>
        <div className="menu-link" onClick={() => toggleDropdown(item.name, isCollapsed)}>
          <span className="menu-icon">{item.icon}</span>
          {!isCollapsed && <span className="menu-label">{item.label}</span>}

          {item.notificationId === 'support' && supportNotification && !isCollapsed && (
            <span className="sidebar-notification-dot" />
          )}

          {!isCollapsed && (
            <FaChevronDown className={`chevron ${isDropdownOpen ? 'open' : ''}`} />
          )}
        </div>

        <ul className={`dropdown-menu ${isDropdownOpen ? 'open' : ''}`}>
          {subItemsPermitidos.map((sub) => (
            <li key={sub.path}>
              <NavLink
                to={sub.path}
                className={({ isActive }) =>
                  isActive || isRouteActive(location.pathname, sub.path) ? 'subitem-active' : ''
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleLinkClick(item.notificationId);
                }}
              >
                {sub.icon && <span className="subitem-icon">{sub.icon}</span>}
                {sub.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className="menu-item">
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          `menu-link ${isActive || isRouteActive(location.pathname, item.path) ? 'active' : ''}`
        }
        onClick={() => handleLinkClick(item.notificationId)}
      >
        <span className="menu-icon">{item.icon}</span>
        {!isCollapsed && <span className="menu-label">{item.label}</span>}
      </NavLink>
    </li>
  );
};

/* ============================================================================
 * Sidebar Principal
 * ============================================================================ */
export default function Sidebar({ user: propsUser, isCollapsed, setIsSidebarCollapsed }) {
  const { user: authUser, perfil } = useAuth();
  const location = useLocation();
  const socket = useSocket();

  const [openDropdown, setOpenDropdown] = useState('');
  const [supportNotification, setSupportNotification] = useState(false);

  // Refs de controle de UX do menu
  const navRef = useRef(null);           // container p/ detectar clique externo
  const manualOpenRef = useRef(false);   // marca abertura manual (evita override do auto-open)

  // Tenant config
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);
  const [tenantNome, setTenantNome] = useState(null);
  const [tenantLogoVersion, setTenantLogoVersion] = useState(Date.now());

  // Busca config do tenant atual com token já carregado no auth
  useEffect(() => {
    if (!authUser) return;

    const ctrl = new AbortController();

    api.get('/api/customize/config', { signal: ctrl.signal })
      .then(({ data }) => {
        const sc = data?.sidebar_config || {};
        const lc = data?.login_config || {};

        const fetchedLogo =
          sc.logo_url ||
          sc.logoUrl ||
          sc.logo ||
          lc.logo_url ||
          null;

        const fetchedNome =
          sc.label ||
          sc.nome_empresa ||
          sc.nome ||
          data?.nome_empresa ||
          null;

        setTenantLogoUrl(fetchedLogo || null);
        setTenantNome(fetchedNome || null);
        setTenantLogoVersion(Date.now());
      })
      .catch((err) => {
        if (!ctrl.signal.aborted) {
          console.warn('[Sidebar] Não foi possível carregar config do tenant:', err.message);
        }
      });

    return () => ctrl.abort();
  }, [authUser]);

  // Atualização em tempo real vinda do SettingsModal
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};

      if ('logo_url' in detail) {
        setTenantLogoUrl(detail.logo_url || null);
        setTenantLogoVersion(Date.now());
      }

      if ('nome_empresa' in detail) {
        setTenantNome(detail.nome_empresa || null);
      }
    };

    window.addEventListener('tenant:config:updated', handler);
    return () => window.removeEventListener('tenant:config:updated', handler);
  }, []);

  const currentUser = useMemo(() => {
    const base = authUser || propsUser || {};
    return {
      ...base,
      perfil: perfil || base.perfil || 'visitante',
      permissoes: normalizePermissions(base.permissoes),
    };
  }, [authUser, propsUser, perfil]);

  // Prioridade: API do tenant > dados do user/token > placeholder
  const rawLogoUrl = tenantLogoUrl || currentUser.logo_url || currentUser.logo || null;
  const displayLogoUrl = formatImageUrl(rawLogoUrl, tenantLogoVersion);
  const displayNome = tenantNome || currentUser.nome_empresa || 'Sistema';

  const menuItems = useMemo(() => [
    { path: '/dashboard', label: 'Dashboard', icon: <FaTachometerAlt />, name: 'dashboard' },
    {
      label: 'Gestão de Frota', icon: <FaTruck />, name: 'gestao_frota',
      isDropdown: true, accessKey: 'frota_view',
      subItems: [
        { path: '/operacoes', label: 'Central de Operações', icon: <FaRoute />, accessKey: 'operacoes_view' },
        { path: '/frota/manutencoes', label: 'Oficinas & Manutenção', icon: <FaWrench />, accessKey: 'manutencao_view' },
        { path: '/frota/push', label: 'Central de Push', icon: <FaPaperPlane />, accessKey: 'frota_push' },
        { path: '/frota/rentabilidade-frota', label: 'Rentabilidade da Frota', icon: <FaTruck />, accessKey: 'fin_rentabilidade' },
        { path: '/frota/diario-bordo', label: 'Diário de Bordo', icon: <FaClipboardCheck />, accessKey: 'diario_bordo_view' },
        { path: '/frota/inventario', label: 'Inventário de Itens', icon: <FaSatelliteDish />, accessKey: 'inventario_view' },
      ],
    },
    {
      label: 'Cadastro', icon: <FaList />, name: 'cadastro',
      isDropdown: true, accessKey: 'cadastro_view',
      subItems: [
  { path: '/clientes', label: 'Clientes', icon: <FaUserTie />, accessKey: 'cad_clientes' },
  { path: '/motoristas', label: 'Motoristas', icon: <FaIdCard />, accessKey: 'cad_motoristas' },
  { path: '/veiculos', label: 'Veículos', icon: <FaCar />, accessKey: 'cad_veiculos' },
  { path: '/comunicacao', label: 'Comunicação', icon: <FaCommentDots />, accessKey: 'cad_comunicacao' },
  { path: '/analytics-veiculos', label: 'Analytics de Frota', icon: <FaChartLine />, accessKey: 'cad_dados_veiculos' },
],
    },
    {
      label: 'Agenda de Serviços', icon: <FaCalendarAlt />, name: 'agenda',
      isDropdown: true, accessKey: 'agenda_view',
      subItems: [
        { path: '/ordens/fila', label: 'Fila de Ordens', icon: <FaStream />, accessKey: 'ag_fila' },
        { path: '/minhas-ordens', label: 'Minhas Ordens', icon: <FaClipboardList />, accessKey: 'ag_minhas' },
      ],
    },
    {
  label: 'Controle Financeiro', icon: <FaDollarSign />, name: 'financeiro',
  isDropdown: true, accessKey: 'financeiro_view',
  subItems: [
    { path: '/financeiro', label: 'Painel de Controle', icon: <FaTachometerAlt />, accessKey: 'fin_dashboard' },
    { path: '/financeiro/contas-a-pagar', label: 'Contas a Pagar', icon: <FaFileUpload />, accessKey: 'fin_contas_pagar' },
    { path: '/financeiro/contas-a-receber', label: 'Contas a Receber', icon: <FaFileDownload />, accessKey: 'fin_contas_receber' },
    { path: '/financeiro/conciliacao-bancaria', label: 'Conciliação Bancária', icon: <FaCheckDouble />, accessKey: 'fin_conciliacao' },
    { path: '/financeiro/transacoes', label: 'Transações', icon: <FaExchangeAlt />, accessKey: 'fin_transacoes' },
    { path: '/financeiro/pagamentos', label: 'Pagamentos', icon: <FaHandHoldingUsd />, accessKey: 'fin_pagamentos' },
    { path: '/financeiro/gastos', label: 'Controle de Gastos', icon: <FaCreditCard />, accessKey: 'fin_gastos' },
    { path: '/financeiro/fluxo-caixa', label: 'Fluxo de Caixa Projetado', icon: <FaChartLine />, accessKey: 'fin_fluxo_caixa' },
    { path: '/financeiro/gerar-boleto', label: 'Gerar Boleto', icon: <FaFileInvoiceDollar />, accessKey: 'fin_gerar_boleto' },
    { path: '/financeiro/balanco', label: 'Balanço Patrimonial', icon: <FaBalanceScale />, accessKey: 'fin_balanco' },
    { path: '/financeiro/gerenciador-patrimonial', label: 'Gerenciador Patrimonial', icon: <FaLandmark />, accessKey: 'fin_gerenciador_patrimonio' },
    { path: '/financeiro/balancete', label: 'Balancete de Verificação', icon: <FaClipboardList />, accessKey: 'fin_balancete' },
    { path: '/financeiro/dre', label: 'D.R.E. Gerencial', icon: <FaFileInvoiceDollar />, accessKey: 'fin_dre' },
    { path: '/financeiro/plano-contas', label: 'Plano de Contas', icon: <FaRegListAlt />, accessKey: 'fin_plano_contas' },
    { path: '/financeiro/fechamento', label: 'Fechamento Mensal', icon: <FaFileSignature />, accessKey: 'fin_fechamento' },
    { path: '/financeiro/relatorios', label: 'Relatórios Financeiros', icon: <FaRegListAlt />, accessKey: 'fin_relatorios' },
    { path: '/financeiro/rotina-fechamento', label: 'Rotina de Fechamento', icon: <FaFileInvoiceDollar />, accessKey: 'fin_rotina_fechamento' },
    { path: '/financeiro/acerto-motoristas', label: 'Acerto de Motoristas', icon: <FaNetworkWired />, accessKey: 'fin_acerto_motoristas' },
  ],
},
    {
      label: 'BaaS Hub', icon: <FaMoneyCheckAlt />, name: 'baas_hub',
      isDropdown: true, accessKey: 'baas_view',
      subItems: [
        { path: '/baas/dashboard', label: 'Dashboard', icon: <FaTachometerAlt />, accessKey: 'baas_dashboard' },
        { path: '/baas/cartoes', label: 'Cartões', icon: <FaCreditCard />, accessKey: 'baas_cards' },
        { path: '/baas/carteira', label: 'Conta', icon: <FaLandmark />, accessKey: 'baas_wallet' },
        { path: '/baas/aportes', label: 'Aportes', icon: <FaReceipt />, accessKey: 'baas_aportes' },
        { path: '/baas/monitor', label: 'Monitor', icon: <FaStream />, accessKey: 'baas_monitor' },
        { path: '/baas/conciliacao', label: 'Conciliação', icon: <FaBalanceScale />, accessKey: 'baas_conciliacao' },
                { path: '/baas/transacoes', label: 'Transações', icon: <FaExchangeAlt />, accessKey: 'baas_transacoes' },
      ],
    },
    {
      label: 'Gestão Fiscal', icon: <FaReceipt />, name: 'gestao_fiscal',
      isDropdown: true, accessKey: 'fiscal_view',
      subItems: [
        { path: '/fiscal/dashboard', label: 'Visão Geral', icon: <FaTachometerAlt />, accessKey: 'fiscal_dashboard' },
        { path: '/fiscal/notas', label: 'Central de Notas (NFS-e)', icon: <FaList />, accessKey: 'fiscal_notas' },
        { path: '/fiscal/emitir', label: 'Emissão Avulsa', icon: <FaPlusCircle />, accessKey: 'fiscal_emitir' },
        { path: '/fiscal/parametros', label: 'Parâmetros e Impostos', icon: <FaCog />, accessKey: 'fiscal_parametros' },
        { path: '/fiscal/certificado', label: 'Certificado Digital', icon: <FaKey />, accessKey: 'fiscal_certificado' },
      ],
    },
    {
      label: 'Ferramentas', icon: <FaTools />, name: 'ferramentas',
      isDropdown: true, accessKey: 'ferramentas_view',
      subItems: [
        { path: '/patio', label: 'Gestão de Pátio', icon: <FaParking />, accessKey: 'patio_gestao' },
        { path: '/admin/config/patio', label: 'Config. Pátio', icon: <FaCog />, accessKey: 'patio_config' },
        { path: '/quadro-tarefas', label: 'Quadro de Tarefas', icon: <FaColumns />, accessKey: 'fer_tarefas' },
        { path: '/base-conhecimento', label: 'Base de Conhecimento', icon: <FaBookOpen />, accessKey: 'fer_conhecimento' },
        { path: '/simulador', label: 'Simulador', icon: <FaCalculator />, accessKey: 'simulador' },
        { path: '/relatorios', label: 'Relatórios', icon: <FaFileAlt />, accessKey: 'relatorios' },
        { path: '/ferramentas/regente', label: 'Regente (IA)', icon: <FaRobot />, accessKey: 'fer_regente' },
      ],
    },
    {
      label: 'Integrações', icon: <FaPlug />, name: 'integracoes',
      isDropdown: true, accessKey: 'integracoes_view',
      subItems: [
        { path: '/integracoes/seguradoras', label: 'Seguradoras (Entrada)', icon: <FaShieldAlt />, accessKey: 'integracoes_seguradoras' },
        { path: '/integracoes/portal-api', label: 'Portal da API', icon: <FaCode />, accessKey: 'integracoes_portal' },
        { path: '/integracoes/api-console', label: 'Console de Tráfego & APIs', icon: <FaNetworkWired />, accessKey: 'integracoes_api_console' },
      ],
    },
    {
      label: 'Analytics', icon: <FaChartLine />, name: 'analytics',
      isDropdown: true, accessKey: 'analytics_menu_view',
      subItems: [
        { path: '/analytics/dados', label: 'Análise de Dados', icon: <FaChartLine />, accessKey: 'analytics_dashboard_view' },
        { path: '/analytics/templates', label: 'Template de Análises', icon: <FaPaperPlane />, accessKey: 'analytics_templates_view' },
      ],
    },
    {
      label: 'Painel de Suporte', icon: <FaHeadset />, name: 'suporte-admin',
      isDropdown: true, accessKey: 'suporte_menu_view', notificationId: 'support',
      subItems: [
        { path: '/suporte-admin/dashboard', label: 'Painel de Chamados', icon: <FaTicketAlt />, accessKey: 'suporte_view' },
        { path: '/suporte-admin/relatorios', label: 'Relatórios', icon: <FaFileAlt />, accessKey: 'suporte_relatorios' },
        { path: '/suporte-admin/gmud', label: 'Gestão de Mudanças', icon: <FaExchangeAlt />, accessKey: 'suporte_gmud' },
        { path: '/suporte-admin/configuracoes', label: 'Configurações', icon: <FaCog />, accessKey: 'suporte_configuracoes' },
        { path: '/suporte-admin/satisfacao', label: 'Pesq. de Satisfação', icon: <FaClipboardCheck />, accessKey: 'suporte_satisfacao' },
      ],
    },
    {
      label: 'Corporativo', icon: <FaBuilding />, name: 'corporativo',
      isDropdown: true, accessKey: 'corporativo_view',
      subItems: [
        { path: '/corporativo/fornecedores', label: 'Fornecedores', icon: <FaUserTie />, accessKey: 'corp_fornecedores' },
        { path: '/corporativo/cotacoes', label: 'Gestão de Cotações', icon: <FaFileInvoiceDollar />, accessKey: 'corp_cotacoes' },
        { path: '/corporativo/compras', label: 'Compras (PO)', icon: <FaShoppingCart />, accessKey: 'corp_compras' },
        { path: '/corporativo/estoque', label: 'Gestão de Estoque', icon: <FaGasPump />, accessKey: 'corp_estoque' },
        { path: '/corporativo/recebimentos', label: 'Recebimento & Conferência', icon: <FaClipboardList />, accessKey: 'corp_recebimentos' },
        { path: '/corporativo/aprovacoes', label: 'Aprovações & Alçadas', icon: <FaClipboardCheck />, accessKey: 'corp_aprovacoes' },
        { path: '/corporativo/juridico', label: 'Jurídico & Contratos', icon: <FaGavel />, accessKey: 'corp_juridico' },
        { path: '/corporativo/qualidade', label: 'Qualidade (SGQ)', icon: <FaClipboardCheck />, accessKey: 'corp_qualidade' },
      ],
    },
    {
      label: 'Gestão de Riscos', icon: <FaShieldAlt />, name: 'gestao_riscos',
      isDropdown: true, accessKey: 'riscos_view',
      subItems: [
        { path: '/riscos/sinistros', label: 'Sinistros & Avarias', icon: <FaGavel />, accessKey: 'riscos_sinistros' },
        { path: '/riscos/recuperacao', label: 'Recuperação de Crédito', icon: <FaHandHoldingUsd />, accessKey: 'riscos_recuperacao' },
      ],
    },
    {
      label: 'Gestão de RH', icon: <FaIdBadge />, name: 'rh',
      isDropdown: true, accessKey: 'rh_completo',
      subItems: [
        { path: '/rh/base-funcionarios', label: 'Base de Funcionários', icon: <FaUsersCog />, accessKey: 'rh_base_funcionarios' },
        { path: '/rh/folha-pagamento', label: 'Folha de Pagamento', icon: <FaFileInvoiceDollar />, accessKey: 'rh_folha_pagamento' },
        { path: '/rh/advertencias', label: 'Central de Advertência', icon: <FaFileAlt />, accessKey: 'rh_advertencias' },
        { path: '/rh/rescisoes', label: 'Rescisões e Demissões', icon: <FaFileAlt />, accessKey: 'rh_rescisoes' },
        { path: '/rh/engajamento', label: 'Portal de Engajamento', icon: <FaFileAlt />, accessKey: 'rh_engajamento' },
        { path: '/rh/vagas', label: 'Gestão de Vagas', icon: <FaFileAlt />, accessKey: 'rh_vagas' },
        { path: '/rh/analytics', label: 'People Analytics', icon: <FaChartLine />, accessKey: 'rh_analytics' },
        { path: '/rh/treinamento', label: 'Treinamento & Dev.', icon: <FaCalendarCheck />, accessKey: 'rh_treinamento' },
        { path: '/rh/desempenho', label: 'Avaliação de Desempenho', icon: <FaClipboardList />, accessKey: 'rh_desempenho' },
        { path: '/rh/beneficios', label: 'Saúde e Benefícios', icon: <FaClipboardCheck />, accessKey: 'rh_beneficios' },
        { path: '/rh/ponto', label: 'Controle de Ponto', icon: <FaCalendarCheck />, accessKey: 'rh_ponto' },
        { path: '/rh/organograma', label: 'Organograma', icon: <FaRegListAlt />, accessKey: 'rh_organograma' },
      ],
    },
    {
      label: 'Administração', icon: <FaUserShield />, name: 'administracao',
      isDropdown: true, accessKey: 'adm_view',
      subItems: [
        { path: '/usuarios', label: 'Gerenciar Usuários', icon: <FaUsersCog />, accessKey: 'adm_usuarios' },
        { path: '/gestao-permissoes', label: 'Controle de Acessos', icon: <FaUserCheckSolid />, accessKey: 'adm_acessos' },
        { path: '/gestao-conteudo', label: 'Gestão de Conteúdo', icon: <FaNewspaper />, accessKey: 'adm_conteudo' },
        { path: '/gestao-tags', label: 'Gestão de Tags', icon: <FaTags />, accessKey: 'adm_tags' },
        { path: '/gestao-templates', label: 'Gestão de Templates', icon: <FaLayerGroup />, accessKey: 'adm_templates' },
        { path: '/logs', label: 'Controle de Logs', icon: <FaTerminal />, accessKey: 'adm_logs' },
      ],
    },
  ], []);

  const finalMenuItems = useMemo(() => {
    const items = [...menuItems];

    // [PORTAL] Portal Corporativo — exclusivo do tenant 1 (admin_geral sempre).
    // Default do tenant, sem exigir accessKey. Inserido logo após o Dashboard.
    if (currentUser.cliente_id === PORTAL_TENANT_ID || currentUser.perfil === 'admin_geral') {
      items.splice(1, 0, {
        path: '/portal',
        label: 'Portal Corporativo',
        icon: <FaColumns />,
        name: 'portal_corporativo',
      });
    }

    if (currentUser.perfil === 'admin_geral') {
      items.push({
        label: 'System Hub',
        icon: <FaShieldAlt />,
        name: 'system_hub',
        isDropdown: true,
        subItems: [
          { path: '/system-hub/dashboard', label: 'Visão Geral', icon: <FaGlobeAmericas /> },
          { path: '/system-hub/clientes', label: 'Gestão de Clientes', icon: <FaUserCheck /> },
          { path: '/system-hub/billing', label: 'Cobrança & Assinaturas', icon: <FaReceipt /> },
          { path: '/system-hub/solicitacoes', label: 'Solicitações de Licença', icon: <FaKey /> },
          { path: '/system-hub/updates', label: 'Novidades do Sistema', icon: <FaBullhorn /> },
          { path: '/system-hub/anuncios', label: 'Anúncios Globais', icon: <FaBroadcastTower /> },
          { path: '/system-hub/configuracoes', label: 'Configurações', icon: <FaTools /> },
          { path: '/system-hub/monitor', label: 'Monitoramento (NOC)', icon: <FaDesktop /> },
          { path: '/system-hub/vendas', label: 'Gestão de Vendas (CRM)', icon: <FaHandHoldingUsd /> },
          { path: '/system-hub/nexus', label: 'Nexus Twin (Sandbox)', icon: <FaVial /> },
          { path: '/system-hub/forensics', label: 'Compliance Forensics', icon: <FaFingerprint /> },
          { path: '/system-hub/site', label: 'Site Institucional', icon: <FaGlobeAmericas /> },
        ],
      });
    }

    return items;
  }, [menuItems, currentUser.perfil, currentUser.cliente_id]);

  useEffect(() => {
    if (!socket) return;

    if (['admin', 'admin_geral', 'financeiro', 'operacional', 'rh'].includes(currentUser.perfil)) {
      const handler = () => setSupportNotification(true);
      socket.on('support_notification', handler);
      return () => socket.off('support_notification', handler);
    }
  }, [socket, currentUser.perfil]);

  const toggleDropdown = (name, wasCollapsed) => {
    if (wasCollapsed) {
      // Abrindo a partir do estado colapsado: expande e respeita a escolha manual
      manualOpenRef.current = true;
      setIsSidebarCollapsed(false);
      setOpenDropdown(name);
    } else {
      // Acordeão: abrir um fecha o outro automaticamente
      setOpenDropdown((prev) => (prev === name ? '' : name));
    }
  };

  // Auto-abre o grupo da rota ativa — SOMENTE quando a rota ou o estado
  // colapsado mudam. Antes, finalMenuItems/currentUser (referências que mudam
  // a cada render) estavam nas deps: o efeito re-disparava em renders espúrios
  // e "puxava" o dropdown de volta para o da rota, brigando com o clique manual.
  // Por isso ficam fora das deps de propósito (lidos, não observados).
  useEffect(() => {
    if (isCollapsed) {
      setOpenDropdown('');
      return;
    }

    // Abertura manual ao expandir tem prioridade nesta passada
    if (manualOpenRef.current) {
      manualOpenRef.current = false;
      return;
    }

    const activeParent = finalMenuItems.find(
      (item) =>
        item.isDropdown &&
        item.subItems?.some(
          (sub) => canAccess(currentUser, sub.accessKey) && isRouteActive(location.pathname, sub.path)
        )
    );

    setOpenDropdown(activeParent ? activeParent.name : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isCollapsed]);

  // Ao clicar fora da sidebar EXPANDIDA: fecha qualquer guia aberta E recolhe
  // a barra (comportamento de gaveta — clique fora dispensa tudo). Ativo
  // sempre que expandida, independente de haver guia aberta. No modo colapsado
  // o submenu é flutuante via :hover, controlado por CSS.
  useEffect(() => {
    if (isCollapsed) return;

    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown('');
        setIsSidebarCollapsed(true);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isCollapsed, setIsSidebarCollapsed]);

  const handleToggleSidebar = () => setIsSidebarCollapsed((prev) => !prev);
  const handleLinkClick = (notifId) => {
    if (notifId === 'support') setSupportNotification(false);
  };

  return (
    <nav ref={navRef} className={`sidebar ${isCollapsed ? 'collapsed' : 'expanded'}`}>
      <LogoHeader
        logoUrl={displayLogoUrl}
        nome={displayNome}
        isCollapsed={isCollapsed}
      />

      <button onClick={handleToggleSidebar} className="sidebar-toggle" type="button">
        {isCollapsed ? '»' : '«'}
      </button>

      <ul className="sidebar-menu">
        {finalMenuItems.map((item) => (
          <MenuItem
            key={item.name}
            item={item}
            isCollapsed={isCollapsed}
            openDropdown={openDropdown}
            toggleDropdown={toggleDropdown}
            handleLinkClick={handleLinkClick}
            location={location}
            supportNotification={supportNotification}
            user={currentUser}
          />
        ))}
      </ul>
    </nav>
  );
}